import { useCallback, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import type { MediaAsset } from '../types';
import { formatBytes, formatDate } from '../format';
import { Button, Empty, ErrorNotice, Panel, Skeleton, Status } from '../ui';
import { usePanel } from './persona-extras';

const KIND_LABELS: Record<string, string> = { image: 'صورة', audio: 'صوت' };
const DIRECTION_LABELS: Record<string, string> = { incoming: 'وارد', outgoing: 'صادر' };
const ORIGIN_LABELS: Record<string, string> = {
  upload: 'رفع من المستخدم', voice_note: 'رسالة صوتية', generated: 'توليد',
  internet: 'إنترنت', ambient: 'محيط'
};
const STATUS_LABELS: Record<string, string> = {
  ready: 'جاهز', uploading: 'قيد الرفع', incomplete: 'غير مكتمل', deleted: 'محذوف', failed: 'فشل'
};

/** Reads a File into base64 without blowing the call stack on large payloads. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

export function MediaLibraryPage() {
  const [filter, setFilter] = useState<'all' | 'image' | 'audio' | 'trash'>('all');
  const loader = useCallback(async () => {
    const page = await api.mediaAssets(filter === 'image' || filter === 'audio' ? filter : undefined, filter === 'trash');
    return filter === 'trash' ? { ...page, items: page.items.filter((item) => item.deletedAt) } : page;
  }, [filter]);
  const { data, error, loading, reload } = usePanel(loader);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [uploading, setUploading] = useState(false);

  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const file = form.get('file');
    if (!(file instanceof File) || file.size === 0) { setNotice('اختر ملفًا أولًا.'); return; }
    const kind = file.type.startsWith('image/') ? 'image' : file.type.startsWith('audio/') ? 'audio' : null;
    if (!kind) { setNotice('نوع الملف غير مدعوم (صور PNG/JPEG/WebP أو صوت).'); return; }
    const direction = String(form.get('direction') ?? 'incoming') === 'outgoing' ? 'outgoing' : 'incoming';
    const origin = kind === 'audio' ? 'voice_note' : 'upload';
    setUploading(true); setNotice('');
    try {
      await api.uploadMedia({ kind, direction, origin, mimeType: file.type, dataBase64: await fileToBase64(file) });
      setNotice('رُفع الوسيط إلى مكتبتك.');
      formElement.reset();
      reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setUploading(false); }
  }

  async function trash(asset: MediaAsset) {
    setBusy(asset.id); setNotice('');
    try { await api.trashMediaAsset(asset.id); setNotice('نُقل الوسيط إلى السلة.'); reload(); }
    catch (caught) { setNotice(errorMessage(caught)); } finally { setBusy(''); }
  }
  async function purge(asset: MediaAsset) {
    if (!confirm('حذف نهائي للوسيط ' + asset.id.slice(0, 8) + ' وملفه؟ لا يمكن التراجع.')) return;
    setBusy(asset.id); setNotice('');
    try { await api.purgeMediaAsset(asset.id); setNotice('حُذف الوسيط وملفه نهائيًا.'); reload(); }
    catch (caught) { setNotice(errorMessage(caught)); } finally { setBusy(''); }
  }
  async function restore(asset: MediaAsset) {
    setBusy(asset.id); setNotice('');
    try { await api.restoreMediaAsset(asset.id); setNotice('أُعيد الوسيط إلى المحادثة والمكتبة.'); reload(); }
    catch (caught) { setNotice(errorMessage(caught)); } finally { setBusy(''); }
  }

  const items = data?.items ?? [];
  return <><PageHeading />
    <Panel title="مكتبة الوسائط" intro="صورك وتسجيلاتك الصوتية مع الاحتفاظ أو الحذف بنطاق واضح."
      actions={<div className="inline-actions">
        {(['all', 'image', 'audio', 'trash'] as const).map((value) => <Button key={value} variant={filter === value ? 'primary' : 'secondary'} onClick={() => setFilter(value)}>{value === 'all' ? 'الكل' : value === 'trash' ? 'السلة' : KIND_LABELS[value]}</Button>)}
      </div>}>
      <form className="form-grid" onSubmit={upload}>
        <label className="full">ملف صورة أو صوت<input name="file" type="file" accept="image/png,image/jpeg,image/webp,audio/webm,audio/ogg,audio/mpeg,audio/mp4,audio/wav" /></label>
        <label>الاتجاه<select name="direction" defaultValue="incoming"><option value="incoming">وارد</option><option value="outgoing">صادر</option></select></label>
        <Button type="submit" busy={uploading}>رفع إلى المكتبة</Button>
        {notice ? <p className="muted" role="status">{notice}</p> : null}
      </form>
      {error ? <ErrorNotice message={error} onRetry={reload} /> : loading ? <Skeleton lines={4} /> : items.length ? <div className="card-list">{items.map((asset) => <article key={asset.id}>
        <div className="row-between"><strong>{KIND_LABELS[asset.kind] ?? asset.kind} — {DIRECTION_LABELS[asset.direction] ?? asset.direction} — {ORIGIN_LABELS[asset.origin] ?? asset.origin}</strong><Status tone={asset.status === 'ready' ? 'good' : asset.status === 'deleted' ? 'bad' : 'warning'}>{STATUS_LABELS[asset.status] ?? asset.status}</Status></div>
        {!asset.deletedAt && asset.kind === 'image' ? <img className="media-preview" src={api.mediaUrl(asset.id)} alt={asset.transcript ?? 'صورة من مكتبة رامي'} loading="lazy" /> : null}
        {!asset.deletedAt && asset.kind === 'audio' ? <audio className="media-preview" controls preload="none" src={api.mediaUrl(asset.id)} /> : null}
        <p className="muted">{formatBytes(asset.byteSize)} — {formatDate(asset.createdAt)}{asset.durationMs ? ' — ' + Math.round(asset.durationMs / 1000) + ' ث' : ''}</p>
        {asset.transcriptStatus !== 'not_requested' ? <p className="muted">التفريغ: {asset.transcriptStatus === 'ready' ? asset.transcript : asset.transcriptStatus === 'failed' ? 'تعذر' : asset.transcriptStatus === 'blocked' ? 'أُوقف بالحذف' : 'قيد المعالجة'}</p> : null}
        {asset.conversationId ? <a href={'/?conversation=' + encodeURIComponent(asset.conversationId)}>فتح المحادثة المرتبطة</a> : <span className="muted">غير مرتبط بمحادثة</span>}
        <div className="inline-actions">
          {asset.deletedAt ? <Button variant="secondary" busy={busy === asset.id} onClick={() => void restore(asset)}>استعادة</Button> : <Button variant="secondary" busy={busy === asset.id} onClick={() => void trash(asset)}>نقل إلى السلة</Button>}
          <Button variant="danger" busy={busy === asset.id} onClick={() => void purge(asset)}>حذف نهائي</Button>
        </div>
      </article>)}</div> : <Empty title="لا وسائط بعد" />}
    </Panel>
    <Panel title="قدرات الوسائط" intro="الحالة الصادقة لما هو مفعّل فعليًا في هذه النسخة.">
      <div className="status-list">
        <div className="status-item"><span>تحليل الصور (رؤية)</span><Status tone="warning">غير مفعّلة — تنتظر مزودًا</Status></div>
        <div className="status-item"><span>توليد الصور</span><Status tone="warning">غير مفعّلة — تنتظر مزودًا</Status></div>
        <div className="status-item"><span>بحث صور إنترنت</span><Status tone="warning">غير مفعّلة — تنتظر مزودًا</Status></div>
        <div className="status-item"><span>رفع وعرض وحذف الوسائط</span><Status tone="good">مفعّل محليًا</Status></div>
      </div>
    </Panel>
  </>;
}

function PageHeading() {
  return <header className="page-title"><p className="eyebrow">لوحة المالك</p><h1>مكتبة الوسائط</h1><p>صور وتسجيلات محفوظة محليًا بصلاحيات المالك، مع إمكانية الحذف بنطاق محدد.</p></header>;
}
